import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { sessionAPI, quizAPI } from '../services/api';
import { connectSocket } from '../services/socket';
import './PlayQuiz.css';

const LoggedInPlayQuiz = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [socket, setSocket] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [timeLeft, setTimeLeft] = useState(20);
  const [username, setUsername] = useState(location.state?.username || null);
  const autoStart = location.state?.autoStart || false;
  const [studentJoined, setStudentJoined] = useState(false);
  const studentJoinedRef = useRef(false);
  const initialLoadRef = useRef(false);
  const autoStartedRef = useRef(false);
  const [showKnownAnimation, setShowKnownAnimation] = useState(false);

  useEffect(() => {
    const socketInstance = connectSocket();
    setSocket(socketInstance);

    // Check if already connected
    if (socketInstance.connected) {
      setSocketConnected(true);
    }

    const handleConnect = () => {
      setSocketConnected(true);
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    socketInstance.on('connect', handleConnect);
    socketInstance.on('disconnect', handleDisconnect);

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    socketInstance.on('session-joined', () => {
      // Session joined successfully
    });

    socketInstance.on('quiz-started', (data) => {
      setQuizStarted(true);
      if (data && data.question) {
        setCurrentQuestion(data.question);
        setCurrentQuestionIndex(data.questionIndex || 0);
        setTimeLeft(data.question.timeLimit || 20);
      } else {
        console.error('Quiz started but no question data received');
      }
    });

    socketInstance.on('next-question', (data) => {
      // Only update if teacher is ahead or if student hasn't manually navigated
      if (data && data.question && data.questionIndex >= currentQuestionIndex) {
        setCurrentQuestion(data.question);
        setCurrentQuestionIndex(data.questionIndex);
        setSelectedAnswer(null);
        setAnswered(false);
        setTimeLeft(data.question.timeLimit || 20);
        setScore(0);
      }
    });

    socketInstance.on('quiz-completed', (data) => {
      setQuizCompleted(true);
      setLeaderboard(data.leaderboard || []);
    });

    socketInstance.on('answer-received', (data) => {
      setScore(data.points);
      setTotalScore(data.totalScore);
      setAnswered(true);
    });

    socketInstance.on('error', (data) => {
      console.error('Socket error:', data);
      alert(data.message || 'An error occurred');
    });

    // Load session data first (doesn't require socket)
    loadSession();

    return () => {
      socketInstance.off('connect', handleConnect);
      socketInstance.off('disconnect', handleDisconnect);
      // Don't disconnect - let the socket service manage it
      // socketInstance.disconnect();
    };
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const sessionRes = await sessionAPI.getSession(sessionId);
      const sessionData = sessionRes.data;
      setSession(sessionData);
      setQuizStarted(sessionData.status === 'active');

      const quizRes = await quizAPI.getQuiz(sessionData.quizId._id);
      const quizData = quizRes.data;
      setQuiz(quizData);

      // If quiz is already active, load the current question
      if (sessionData.status === 'active' && quizData && quizData.questions && quizData.questions.length > 0) {
        const currentIndex = sessionData.currentQuestionIndex || 0;
        const questions = [...quizData.questions].sort((a, b) => (a.questionIndex || 0) - (b.questionIndex || 0));

        if (questions[currentIndex]) {
          setCurrentQuestion(questions[currentIndex]);
          setCurrentQuestionIndex(currentIndex);
          setTimeLeft(questions[currentIndex].timeLimit || 20);
        }
      }
    } catch (error) {
      console.error('Error loading session:', error);
      alert('Error loading quiz session');
    }
  };

  // Join session when socket is connected and session is loaded
  useEffect(() => {
    if (!socket || !session || !session.pin || studentJoinedRef.current) {
      return;
    }

    const studentUsername = location.state?.username || username || `User${Math.floor(Math.random() * 1000)}`;

    // Store username in state for later use
    if (!username) {
      setUsername(studentUsername);
    }

    const joinSession = () => {
      if (studentJoinedRef.current) return;
      studentJoinedRef.current = true;
      setStudentJoined(true);

      socket.emit('join-session', {
        pin: session.pin,
        userId: null,
        username: studentUsername
      });
    };

    if (socket.connected || socketConnected) {
      joinSession();
    } else {
      const connectHandler = () => joinSession();
      socket.once('connect', connectHandler);

      setTimeout(() => {
        if (!studentJoinedRef.current && socket.connected) {
          socket.off('connect', connectHandler);
          joinSession();
        }
      }, 1000);
    }
  }, [socket, socketConnected, session]);

  // Auto-start quiz if autoStart flag is set (for self-paced quizzes from browse page)
  // Wait until student has joined the session before starting
  useEffect(() => {
    if (autoStart && socket && socketConnected && session && session.status === 'waiting' && studentJoined && !autoStartedRef.current) {
      autoStartedRef.current = true;
      // Auto-start the quiz after a short delay to ensure everything is ready
      setTimeout(() => {
        if (socket && socket.connected) {
          socket.emit('start-quiz', { sessionId });
        }
      }, 500);
    }
  }, [autoStart, socket, socketConnected, session, sessionId, studentJoined]);

  // Load current question if quiz is already active (only on initial load)
  useEffect(() => {
    if (quizStarted && session && session.status === 'active' && quiz && quiz.questions && !initialLoadRef.current) {
      const sessionIndex = session.currentQuestionIndex !== undefined ? session.currentQuestionIndex : 0;
      if (quiz.questions[sessionIndex]) {
        setCurrentQuestion(quiz.questions[sessionIndex]);
        setCurrentQuestionIndex(sessionIndex);
        setTimeLeft(quiz.questions[sessionIndex].timeLimit || 20);
        initialLoadRef.current = true;
      }
    }
  }, [quizStarted, session?.status, quiz?.questions?.length]);

  useEffect(() => {
    if (timeLeft > 0 && quizStarted && !answered && !quizCompleted && currentQuestion) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !answered && currentQuestion) {
      // Time's up, auto-submit no answer
      handleSubmitAnswer(null);
    }
  }, [timeLeft, quizStarted, answered, quizCompleted, currentQuestion]);


  const handleSubmitAnswer = (answerIndex) => {
    if (answered || !currentQuestion) return;

    const timeTaken = (currentQuestion.timeLimit || 20) - timeLeft;
    setSelectedAnswer(answerIndex);
    setAnswered(true);

    // Show animation if correct
    const isCorrectAnswer = answerIndex === currentQuestion.correctAnswer;
    if (isCorrectAnswer) {
      setShowKnownAnimation(true);
      setTimeout(() => setShowKnownAnimation(false), 300);
    }

    if (socket) {
      const studentUsername = location.state?.username || username || `User${Math.floor(Math.random() * 1000)}`;
      socket.emit('submit-answer', {
        sessionId,
        questionIndex: currentQuestionIndex,
        answer: answerIndex,
        timeTaken,
        username: studentUsername
      });
    }

    // Auto-advance to next question after delay
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setTimeout(() => {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        setCurrentQuestion(quiz.questions[nextIndex]);
        setSelectedAnswer(null);
        setAnswered(false);
        setTimeLeft(quiz.questions[nextIndex].timeLimit || 20);
        setScore(0);
        setShowKnownAnimation(false);
      }, 2000); // 2 second delay to show animation and feedback
    } else {
      // Last question - finish quiz after delay
      setTimeout(() => {
        if (socket && autoStart) {
          socket.emit('finish-quiz', { sessionId });
        } else {
          if (window.opener || window.history.length <= 1) {
            window.close();
            setTimeout(() => {
              if (!document.hidden) {
                navigate('/');
              }
            }, 100);
          } else {
            navigate('/');
          }
        }
      }, 2000);
    }
  };

  if (!session || !quiz) {
    return <div className="loading">Loading...</div>;
  }

  if (!quizStarted && session.status === 'waiting') {
    return (
      <div className="play-quiz">
        <div className="waiting-screen card">
          <h2>Waiting for quiz to start...</h2>
          <p>You're all set! The host will start the quiz soon.</p>
          <div className="quiz-info">
            <h3>{quiz.title}</h3>
            <p>Level: {quiz.level || (quiz.difficulty === 'beginner' ? 'A1' : quiz.difficulty === 'intermediate' ? 'B1' : quiz.difficulty === 'advanced' ? 'C1' : 'A1')}</p>
            <p>Skill: {quiz.skill || (quiz.category === 'reading' ? 'Reading' : quiz.category === 'listening' ? 'Listening' : 'Reading')}</p>
            <p>Task: {quiz.task || (quiz.category === 'vocabulary' ? 'Vocabulary' : quiz.category === 'grammar' ? 'Grammar' : quiz.category || 'Vocabulary')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (quizCompleted) {
    return (
      <div className="play-quiz">
        <div className="results-screen card">
          <h2>Quiz Completed! 🎉</h2>
          <div className="your-score">
            <h3>Your Score</h3>
            <div className="score-display">{totalScore} points</div>
          </div>
          <div className="leaderboard">
            <h3>Leaderboard</h3>
            <ol>
              {leaderboard.filter(entry => entry.username !== 'Host').map((entry, idx) => (
                <li key={idx} className={entry.username === username ? 'you' : ''}>
                  <span className="rank">{idx + 1}</span>
                  <span className="name">{entry.username}</span>
                  <span className="score">{entry.score} pts</span>
                </li>
              ))}
            </ol>
          </div>
          <button onClick={() => navigate('/join')} className="btn btn-primary">
            Join Another Quiz
          </button>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="loading">Waiting for question...</div>;
  }

  const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

  return (
    <div className="play-quiz">
      <div className="play-header">
        <div className="play-header-content">
          <div className="score-display-small">Score: {totalScore}</div>
          <div className="play-header-title">Question</div>
          <div className="timer">{timeLeft}s</div>
        </div>
        <div className="question-counter" style={{ marginTop: '0.5rem', color: 'black' }}>
          Question {currentQuestionIndex + 1} of {quiz.questions.length}
        </div>
      </div>

      <div className="question-display">
        <h2 className="question-text">{currentQuestion.questionText}</h2>
        {currentQuestion.imageUrl ? (
          <div className="question-image-container">
            <img src={currentQuestion.imageUrl} alt="Question" className="question-image" />
          </div>
        ) : (
          <div className="question-image-container"></div>
        )}
        <div className="options-grid">
          {showKnownAnimation && (
            <div className="known-text-overlay">Known</div>
          )}
          {currentQuestion.options.map((option, idx) => {
            let optionClass = 'option-button';
            if (answered) {
              if (idx === currentQuestion.correctAnswer) {
                optionClass += ' correct';
              } else if (idx === selectedAnswer && !isCorrect) {
                optionClass += ' incorrect';
              }
            } else if (selectedAnswer === idx) {
              optionClass += ' selected';
            }

            return (
              <button
                key={idx}
                className={optionClass}
                onClick={() => !answered && handleSubmitAnswer(idx)}
                disabled={answered}
              >
                <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                <span className="option-text">{option}</span>
              </button>
            );
          })}
        </div>
        {answered && score > 0 && (
          <div className={`answer-feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
            <div className="points-earned">+{score} points</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoggedInPlayQuiz;

